import * as React from "react";
import { Alert, Button, Modal, Switch, Tag, Typography } from "antd";
import { normIso, CurrencyRefDto } from "../domain/currency/normalizeCurrency";

const { Text } = Typography;

export interface AddCurrencyModalProps {
  open: boolean;
  onCancel: () => void;
  onApply: (currencyIsos: string[]) => void;
  centreCurrencyIso?: string;
  enabledCurrencies: string[];
  currencies: CurrencyRefDto[];
}

const buildCurrencyRows = (
  allCurrencies: CurrencyRefDto[],
  centreIso?: string
): CurrencyRefDto[] => {
  const map = new Map<string, CurrencyRefDto>();

  for (const currency of allCurrencies ?? []) {
    const iso = normIso(currency?.isoCode);
    if (!iso) continue;
    map.set(iso, { ...currency, isoCode: iso, id: currency.id ?? iso });
  }

  if (centreIso && !map.has(centreIso)) {
    map.set(centreIso, {
      id: centreIso,
      isoCode: centreIso,
      name: centreIso,
      symbol: "",
    });
  }

  return [...map.values()].sort((a, b) => a.isoCode.localeCompare(b.isoCode));
};

export default function AddCurrencyModal({
  open,
  onCancel,
  onApply,
  centreCurrencyIso,
  enabledCurrencies,
  currencies,
}: AddCurrencyModalProps) {
  const [selectedEnabledIsos, setSelectedEnabledIsos] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    if (open) {
      setSelectedEnabledIsos(
        new Set(enabledCurrencies.map((iso) => normIso(iso)).filter(Boolean))
      );
    }
  }, [open, enabledCurrencies]);

  const centreIso = normIso(centreCurrencyIso);
  const enabledSet = React.useMemo(
    () => new Set(enabledCurrencies.map((iso) => normIso(iso)).filter(Boolean)),
    [enabledCurrencies]
  );

  const rows = React.useMemo(
    () => buildCurrencyRows(currencies, centreIso),
    [currencies, centreIso]
  );

  const hasChanges = React.useMemo(() => {
    if (selectedEnabledIsos.size !== enabledSet.size) return true;
    for (const iso of selectedEnabledIsos) {
      if (!enabledSet.has(iso)) return true;
    }
    return false;
  }, [selectedEnabledIsos, enabledSet]);

  const toggleCurrency = (iso: string, next: boolean) => {
    setSelectedEnabledIsos((prev) => {
      const updated = new Set(prev);
      if (next) updated.add(iso);
      else updated.delete(iso);
      return updated;
    });
  };

  const handleApply = () => {
    if (!hasChanges) return;
    Modal.confirm({
      title: "Apply currency changes?",
      content:
        "Disabled currencies will be removed from the Pricelist.",
      okText: "Apply",
      cancelText: "Cancel",
      onOk: () => {
        onApply([...selectedEnabledIsos.values()]);
      },
    });
  };

  return (
    <Modal
      title="Manage currency"
      open={open}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="apply"
          type="primary"
          disabled={!hasChanges}
          onClick={handleApply}
        >
          Apply
        </Button>,
      ]}
    >
      <Alert
        type="warning"
        showIcon
        title="Enable or disable currencies for this Pricelist."
        style={{ marginBottom: 16 }}
      />

      <div className="add-currency-list">
        {rows.map((currency) => {
          const iso = normIso(currency.isoCode);
          const isDefault = !!centreIso && iso === centreIso;
          const isSelected = selectedEnabledIsos.has(iso);
          const label = `${iso} - ${currency.name || iso}`;

          return (
            <div key={iso} className="add-currency-row">
              <div className="add-currency-label">
                <Text>{label}</Text>
                {isDefault ? <Tag color="blue">default</Tag> : null}
              </div>
              <Switch
                checked={isSelected}
                disabled={isDefault}
                checkedChildren="Enabled"
                unCheckedChildren="Disabled"
                onChange={(checked) => toggleCurrency(iso, checked)}
              />
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
